import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  SxProps,
  Typography,
} from '@mui/material';
import {
  CloudDownload as CloudDownloadIcon,
  PlayArrow as PlayArrowIcon,
} from '@mui/icons-material';
import ReactPlayer from 'react-player';

import {
  TP_COLORS,
  TPlatformSessionZoomRecordingItem,
} from '../../../../constants';

interface ViewZoomRecordingsModalProps {
  open: boolean;
  onClose: () => void;
  zoomRecordings: TPlatformSessionZoomRecordingItem[];
}

const getFormatedTabName = (
  tabName: TPlatformSessionZoomRecordingItem['type']
) => {
  let newTabName: string = tabName;

  switch (tabName) {
    case 'MP4':
      newTabName = 'Videos';
      break;
    case 'M4A':
      newTabName = 'Audios';
      break;
    case 'CHAT':
      newTabName = 'Chats';
      break;
    case 'TRANSCRIPT':
      newTabName = 'Transcripts';
      break;
    default:
      newTabName = tabName;
      break;
  }

  return newTabName;
};

const tabContainerStyles: SxProps = {
  backgroundColor: '#F1F5F9',
  borderRadius: '15px',
  padding: '8px',
  mb: '30px',
};

const tabItemStyles: SxProps = {
  backgroundColor: 'transparent',
  color: '#334155',
  borderRadius: '15px',
  fontWeight: 600,
  minWidth: '100px',
};

const ViewZoomRecordingsModal: React.FC<ViewZoomRecordingsModalProps> = ({
  open,
  onClose,
  zoomRecordings,
}) => {
  // State
  const [selectedTab, setSelectedTab] = useState<
    TPlatformSessionZoomRecordingItem['type'] | null
  >(null);

  const [
    selectedFile,
    setSelectedFile,
  ] = useState<TPlatformSessionZoomRecordingItem | null>(null);

  const tabs: Array<
    TPlatformSessionZoomRecordingItem['type']
  > = zoomRecordings.map((item) => item.type);

  useEffect(() => {
    if (zoomRecordings.length && zoomRecordings[0] && zoomRecordings[0].type) {
      setSelectedTab(zoomRecordings[0].type);
    }
  }, []);

  useEffect(() => {
    if (zoomRecordings.length) {
      const _list = zoomRecordings.filter((item) => item.type === selectedTab);

      if (_list.length) {
        setSelectedFile(_list[0]);
      }
    }
  }, [selectedTab]);

  const renderTableView = () => {
    const _list = zoomRecordings.filter((item) => item.type === selectedTab);

    if (_list && _list.length) {
      return (
        <Stack gap={2}>
          <Typography variant="h6">Playlist</Typography>
          <List sx={{ border: '1px solid #E2E8F0', borderRadius: '7px' }}>
            {_list.map((item, index) => {
              const orderNumber = index + 1;
              const isSelected = selectedFile && selectedFile.url === item.url;

              let name = `${orderNumber}`;

              if (selectedTab === 'MP4') {
                name = `Video ${orderNumber}`;
              } else if (selectedTab === 'M4A') {
                name = `Audio ${orderNumber}`;
              } else if (selectedTab === 'CHAT') {
                name = `Chat ${orderNumber}`;
              } else if (selectedTab === 'TRANSCRIPT') {
                name = `Transcript ${orderNumber}`;
              }

              return (
                <ListItem
                  key={`li-${orderNumber}`}
                  disablePadding
                  secondaryAction={
                    <IconButton aria-label="download" href={item.url}>
                      <CloudDownloadIcon />
                    </IconButton>
                  }
                  onClick={() => {
                    if (!isSelected) {
                      setSelectedFile(item);
                    }
                  }}
                >
                  <ListItemButton>
                    {isSelected ? (
                      <ListItemIcon>
                        <PlayArrowIcon />
                      </ListItemIcon>
                    ) : null}
                    <ListItemText primary={name} />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Stack>
      );
    }

    return null;
  };

  return (
    <>
      <Dialog
        fullWidth
        maxWidth="lg"
        open={open}
        onClose={onClose}
        PaperProps={{ sx: { minHeight: '400px' } }}
      >
        <DialogTitle>Recordings</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12}>
              <Stack direction="row" sx={tabContainerStyles}>
                {tabs.map((tab) => {
                  const isSelected = tab === selectedTab;
                  const tabName = getFormatedTabName(tab);

                  const _tabStyles = {
                    ...tabItemStyles,
                  };

                  if (isSelected) {
                    _tabStyles.backgroundColor = '#1E293B';
                    _tabStyles.color = TP_COLORS.white;
                    _tabStyles['&: hover'] = {
                      backgroundColor: '#1E293B',
                      color: TP_COLORS.white,
                    };
                  }

                  return (
                    <Button
                      key={tabName}
                      size="medium"
                      variant="text"
                      color="inherit"
                      sx={_tabStyles}
                      onClick={() => {
                        setSelectedTab(tab);
                        setSelectedFile(null);
                      }}
                    >
                      {tabName}
                    </Button>
                  );
                })}
              </Stack>
              <Box>
                <Grid container spacing={2}>
                  <Grid item xs={6} lg={8}>
                    <Box
                      sx={{
                        position: 'sticky',
                        top: '30px',
                        '& :first-child': {
                          borderRadius: '12px',
                        },
                      }}
                    >
                      <ReactPlayer
                        className="react-player"
                        // playing
                        controls
                        url={
                          selectedFile && selectedFile.url
                            ? selectedFile.url
                            : undefined
                        }
                        width="100%"
                        height={
                          selectedFile && selectedFile.type === 'M4A'
                            ? 60
                            : 'auto'
                        }
                        style={{
                          margin: 0,
                          borderRadius: '12px',
                        }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={6} lg={4}>
                    {renderTableView()}
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ViewZoomRecordingsModal;
